import { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingDataStore } from '@/stores/onboardingDataStore';
import { upsertShootingPreferences } from '@/services/data/shootingPreferences';
import { InstallationChoice } from './InstallationChoice';
import type { InstallationType } from '@/services/data/shootingPreferences';

interface ShootingSetupStepProps {
  onComplete: () => void;
  setLoading: (loading: boolean) => void;
  onCanProceedChange?: (canProceed: boolean) => void;
}

export interface ShootingSetupStepRef {
  handleContinue: () => void;
}

export const ShootingSetupStep = forwardRef<ShootingSetupStepRef, ShootingSetupStepProps>(
  ({ onComplete, setLoading, onCanProceedChange }, ref) => {
    const { userId } = useAuthStore();
    const shootingPreferences = useOnboardingDataStore((state) => state.shootingPreferences);
    const updateShootingPreferences = useOnboardingDataStore((state) => state.updateShootingPreferences);
    
    // Form state - initialize from preloaded data
    const [installationType, setInstallationType] = useState<InstallationType | null>(
      (shootingPreferences?.installation_type as InstallationType) || null
    );

    // Sync with preloaded data when it becomes available
    useEffect(() => {
      if (shootingPreferences?.installation_type) {
        setInstallationType(shootingPreferences.installation_type as InstallationType);
      }
    }, [shootingPreferences]);

    // Update canProceed when installation changes
    useEffect(() => {
      onCanProceedChange?.(installationType !== null);
    }, [installationType, onCanProceedChange]);

    const saveAndContinue = useCallback(async () => {
      if (!userId) return;
      
      setLoading(true);
      
      // Optimistic update in store
      updateShootingPreferences({ installation_type: installationType });
      
      const result = await upsertShootingPreferences(userId, {
        installation_type: installationType,
      });
      
      setLoading(false);
      
      if (result.success) {
        onComplete();
      }
    }, [userId, installationType, setLoading, onComplete, updateShootingPreferences]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      handleContinue: () => {
        console.log('[ShootingSetup] handleContinue called');
        saveAndContinue();
      },
    }), [saveAndContinue]);

    const handleInstallationSelect = (type: InstallationType | null) => {
      setInstallationType(type);
    };

    return (
      <InstallationChoice
        selected={installationType}
        onSelect={handleInstallationSelect}
        onContinue={saveAndContinue}
      />
    );
  }
);

ShootingSetupStep.displayName = 'ShootingSetupStep';
